import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, MoreVertical, Plus, Search, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

type ApiMbqGroupRow = {
  id: number;
  group_name: string;
  product_id: number[];
  metrics: unknown;
  favourite: boolean;
};

type MatrixRow = {
  key: string;
  marginPercent: string;
  quantity: string;
  sellingPrice: string;
};

function makeRowKey() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function normalizeMetricsToRows(metrics: unknown): MatrixRow[] {
  if (!Array.isArray(metrics)) return [{ key: makeRowKey(), marginPercent: '', quantity: '', sellingPrice: '' }];
  const out: MatrixRow[] = metrics
    .map((m) => {
      if (!m || typeof m !== 'object') return null;
      const qtyRaw = (m as any).quantity;
      const spRaw = (m as any).selling_price;
      const marginRaw = (m as any).margin;

      const qty = qtyRaw === null || qtyRaw === undefined ? '' : String(qtyRaw);
      const sellingPrice = spRaw === null || spRaw === undefined ? '' : String(spRaw);

      let marginPercent = '';
      if (typeof marginRaw === 'number' && Number.isFinite(marginRaw)) {
        // DB is expected to store decimal (0.45). Accept percent too if present.
        const pct = marginRaw <= 1 ? marginRaw * 100 : marginRaw;
        marginPercent = String(Math.round(pct * 100) / 100);
      } else if (typeof marginRaw === 'string' && String(marginRaw).trim()) {
        const num = Number(String(marginRaw).trim());
        if (Number.isFinite(num)) {
          const pct = num <= 1 ? num * 100 : num;
          marginPercent = String(Math.round(pct * 100) / 100);
        }
      }

      return { key: makeRowKey(), marginPercent, quantity: qty, sellingPrice };
    })
    .filter(Boolean) as MatrixRow[];

  return out.length > 0 ? out : [{ key: makeRowKey(), marginPercent: '', quantity: '', sellingPrice: '' }];
}

function buildMetricsPayload(rows: MatrixRow[]) {
  const metrics = (rows || [])
    .map((r) => {
      const qty = Number(String(r.quantity ?? '').trim());
      const sellingPrice = Number(String(r.sellingPrice ?? '').trim());
      const marginPct = Number(String(r.marginPercent ?? '').trim());

      if (!Number.isFinite(qty) || qty <= 0) return null;
      if (!Number.isFinite(sellingPrice) || sellingPrice < 0) return null;
      if (!Number.isFinite(marginPct) || marginPct < 0 || marginPct > 100) return null;

      const margin = Number((marginPct / 100).toFixed(6));
      return { quantity: Math.trunc(qty), selling_price: sellingPrice, margin };
    })
    .filter(Boolean) as Array<{ quantity: number; selling_price: number; margin: number }>;

  // Ensure stable ordering (backend also sorts by quantity during computation)
  metrics.sort((a, b) => a.quantity - b.quantity);
  return metrics;
}

export function MBQManagementPage() {
  const [groups, setGroups] = useState<ApiMbqGroupRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());

  const [editOpen, setEditOpen] = useState(false);
  const [editGroup, setEditGroup] = useState<ApiMbqGroupRow | null>(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [matrixRows, setMatrixRows] = useState<MatrixRow[]>([{ key: makeRowKey(), marginPercent: '', quantity: '', sellingPrice: '' }]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteGroup, setDeleteGroup] = useState<ApiMbqGroupRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter((g) => (g.group_name || '').toLowerCase().includes(q));
  }, [groups, searchQuery]);

  async function loadGroups() {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch('/v1/api/gp-reporter/special-calculation-groups/', { method: 'GET' });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      const data = (await resp.json()) as unknown;
      const arr = Array.isArray(data) ? data : [];
      const parsed: ApiMbqGroupRow[] = arr
        .map((x) => {
          const id = Number((x as any)?.id);
          const group_name = String((x as any)?.group_name ?? '').trim();
          const favourite = Boolean((x as any)?.favourite);
          const product_id_raw = (x as any)?.product_id;
          const product_id = Array.isArray(product_id_raw)
            ? product_id_raw.map((v: any) => Number(v)).filter((n: any) => Number.isFinite(n))
            : [];
          const metrics = (x as any)?.metrics;
          if (!Number.isFinite(id) || !group_name) return null;
          return { id, group_name, favourite, product_id, metrics };
        })
        .filter(Boolean) as ApiMbqGroupRow[];
      setGroups(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load MBQ groups.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadGroups();
  }, []);

  async function setGroupFavourite(params: { groupId: number; favourite: boolean }) {
    const { groupId, favourite } = params;
    const prevFavourite = groups.find((g) => g.id === groupId)?.favourite;
    setUpdatingIds((s) => new Set([...Array.from(s), groupId]));
    setGroups((cur) => cur.map((g) => (g.id === groupId ? { ...g, favourite } : g)));
    try {
      const resp = await fetch(`/v1/api/gp-reporter/special-calculation-groups/${groupId}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ favourite }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
    } catch (e) {
      if (typeof prevFavourite === 'boolean') {
        setGroups((cur) => cur.map((g) => (g.id === groupId ? { ...g, favourite: prevFavourite } : g)));
      }
      setError(e instanceof Error ? e.message : 'Failed to update favourite.');
    } finally {
      setUpdatingIds((s) => {
        const next = new Set(s);
        next.delete(groupId);
        return next;
      });
    }
  }

  function openEditDialog(g: ApiMbqGroupRow) {
    setEditGroup(g);
    setEditGroupName(g.group_name);
    setMatrixRows(normalizeMetricsToRows(g.metrics));
    setSaveError(null);
    setEditOpen(true);
  }

  async function saveGroup() {
    if (!editGroup || saving) return;
    const gn = String(editGroupName ?? '').trim();
    if (!gn) {
      setSaveError('Please enter a group name.');
      return;
    }

    const metrics = buildMetricsPayload(matrixRows);
    if (metrics.length === 0) {
      setSaveError('Please add at least one valid margin matrix row.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    // Optimistic update
    const prev = groups.find((x) => x.id === editGroup.id);
    setGroups((cur) =>
      cur.map((x) =>
        x.id === editGroup.id
          ? {
              ...x,
              group_name: gn,
              metrics,
            }
          : x
      )
    );

    try {
      const resp = await fetch(`/v1/api/gp-reporter/special-calculation-groups/${editGroup.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ group_name: gn, metrics }),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      await loadGroups();
      setEditOpen(false);
      setEditGroup(null);
    } catch (e) {
      if (prev) setGroups((cur) => cur.map((x) => (x.id === prev.id ? prev : x)));
      setSaveError(e instanceof Error ? e.message : 'Failed to update group.');
    } finally {
      setSaving(false);
    }
  }

  function openDeleteDialog(g: ApiMbqGroupRow) {
    setDeleteGroup(g);
    setDeleteError(null);
    setDeleteOpen(true);
  }

  async function deleteSelectedGroup() {
    if (!deleteGroup || deleting) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const resp = await fetch(`/v1/api/gp-reporter/special-calculation-groups/${deleteGroup.id}/`, {
        method: 'DELETE',
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => '');
        throw new Error(`Request failed (${resp.status})${text ? `: ${text}` : ''}`);
      }
      setGroups((cur) => cur.filter((x) => x.id !== deleteGroup.id));
      setDeleteOpen(false);
      setDeleteGroup(null);
    } catch (e) {
      setDeleteError(e instanceof Error ? e.message : 'Failed to delete group.');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header with Search and Refresh */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search MBQ groups..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Button variant="outline" onClick={loadGroups} disabled={loading} className="gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          Refresh
        </Button>
      </div>

      <div className="text-sm text-muted-foreground">
        Showing {filteredGroups.length} of {groups.length} groups
      </div>

      {error && (
        <div className="flex items-start gap-2 text-sm text-destructive">
          <AlertTriangle className="h-4 w-4 mt-0.5" />
          <span className="break-words">{error}</span>
        </div>
      )}

      {/* Table */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Group Name</TableHead>
              <TableHead className="w-[120px] text-center">Favourite</TableHead>
              <TableHead className="w-[90px] text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredGroups.length > 0 ? (
              filteredGroups.map((g) => (
                <TableRow key={g.id}>
                  <TableCell>
                    <div className="text-sm">{g.group_name}</div>
                  </TableCell>
                  <TableCell className="text-center">
                    <Select
                      value={g.favourite ? 'true' : 'false'}
                      onValueChange={(v) => setGroupFavourite({ groupId: g.id, favourite: v === 'true' })}
                      disabled={loading || updatingIds.has(g.id)}
                    >
                      <SelectTrigger className="h-8 w-[110px] text-xs font-normal mx-auto">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Yes</SelectItem>
                        <SelectItem value="false">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-center">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onSelect={() => openEditDialog(g)}>
                          Edit margin matrix
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onSelect={() => openDeleteDialog(g)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} className="py-10 text-center text-muted-foreground">
                  {loading ? 'Loading groups…' : 'No groups found'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Edit MBQ group</DialogTitle>
            <DialogDescription>
              {editGroup ? (
                <>
                  Group id: <span className="font-mono">{editGroup.id}</span>
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col">
            {/* Scrollable content area */}
            <div className="max-h-[60vh] overflow-auto pr-2 space-y-5">
              <div className="space-y-2 max-w-md">
                <Label htmlFor="mbq-group-name">Group name</Label>
                <Input
                  id="mbq-group-name"
                  placeholder="e.g. Teaset"
                  value={editGroupName}
                  onChange={(e) => setEditGroupName(e.currentTarget.value)}
                  className="h-9"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Margin matrix</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs font-normal gap-1"
                    onClick={() =>
                      setMatrixRows((cur) => [
                        ...cur,
                        { key: makeRowKey(), marginPercent: '', quantity: '', sellingPrice: '' },
                      ])
                    }
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add row
                  </Button>
                </div>

                <div className="space-y-3">
                  {matrixRows.map((row, idx) => (
                    <div key={row.key} className="rounded-md border p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="grid grid-cols-12 gap-3">
                            <div className="col-span-12 md:col-span-4">
                              <Label className="text-xs" htmlFor={`marg-${row.key}`}>
                                Margin (%) {matrixRows.length > 1 ? `#${idx + 1}` : ''}
                              </Label>
                              <div className="mt-1 flex items-center gap-2">
                                <Input
                                  id={`marg-${row.key}`}
                                  type="number"
                                  inputMode="decimal"
                                  placeholder="e.g. 45"
                                  value={row.marginPercent}
                                  onChange={(e) => {
                                    const v = e.currentTarget.value;
                                    setMatrixRows((cur) =>
                                      cur.map((r) => (r.key === row.key ? { ...r, marginPercent: v } : r))
                                    );
                                  }}
                                  className="h-9"
                                />
                                <Badge variant="outline" className="font-normal">
                                  %
                                </Badge>
                              </div>
                            </div>

                            <div className="col-span-12 md:col-span-4">
                              <Label className="text-xs" htmlFor={`qty-${row.key}`}>
                                Quantity
                              </Label>
                              <Input
                                id={`qty-${row.key}`}
                                type="number"
                                inputMode="numeric"
                                placeholder="e.g. 2"
                                value={row.quantity}
                                onChange={(e) => {
                                  const v = e.currentTarget.value;
                                  setMatrixRows((cur) =>
                                    cur.map((r) => (r.key === row.key ? { ...r, quantity: v } : r))
                                  );
                                }}
                                className="h-9 mt-1"
                              />
                            </div>

                            <div className="col-span-12 md:col-span-4">
                              <Label className="text-xs" htmlFor={`sp-${row.key}`}>
                                Selling price
                              </Label>
                              <Input
                                id={`sp-${row.key}`}
                                type="number"
                                inputMode="decimal"
                                placeholder="e.g. 199.90"
                                value={row.sellingPrice}
                                onChange={(e) => {
                                  const v = e.currentTarget.value;
                                  setMatrixRows((cur) =>
                                    cur.map((r) => (r.key === row.key ? { ...r, sellingPrice: v } : r))
                                  );
                                }}
                                className="h-9 mt-1"
                              />
                            </div>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 mt-5"
                          disabled={matrixRows.length <= 1}
                          onClick={() => setMatrixRows((cur) => cur.filter((r) => r.key !== row.key))}
                          aria-label="Remove row"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-muted-foreground">
                  Rows are saved as decimal margins (e.g. 45% → 0.45).
                </p>
              </div>

              {saveError && (
                <div className="flex items-start gap-2 text-sm text-destructive">
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <span className="break-words">{saveError}</span>
                </div>
              )}
            </div>

            {/* Footer pinned */}
            <div className="mt-4 flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setEditOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={saveGroup} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete MBQ group?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteGroup ? (
                <>
                  This will permanently delete <span className="font-medium">{deleteGroup.group_name}</span> (id{' '}
                  <span className="font-mono">{deleteGroup.id}</span>).
                </>
              ) : (
                'This will permanently delete the selected group.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {deleteError && (
            <div className="flex items-start gap-2 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span className="break-words">{deleteError}</span>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteSelectedGroup} disabled={deleting}>
              {deleting ? 'Deleting…' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

